// Refactor 02/2026 — extrahiert aus AppointmentsPage.js.
// Verwaltet alle Calendar-Daten (Appointments, Mindbody-Slots, Trainer,
// Services, Customers, Anamnese-Status, Geburtstage, Payment-Colors,
// Ferien/Blocks/Exceptions, Öffnungszeiten und Kalender-Stunden).
//
// Bereitstellt:
//   - State (Read-Only via Return-Value)
//   - Setter (für Stellen die State direkt anpassen müssen,
//     z.B. nach optimistic delete im Page-Component)
//   - Fetcher (imperativ aufrufbar nach Booking/Edit/Delete/Move)
//   - handleMindbodySync (Button im Header)
//   - isStudioOpenOnDate (Helper basierend auf studioOpeningHours)
//
// Lade-Sequenz:
//   1. Statische Daten beim Mount (Settings, Services, Customers, Birthdays,
//      PaymentColors) — einmalig.
//   2. Dynamische Daten beim Datum-/View-/Trainer-Wechsel (Trainer,
//      Appointments inkl. MindbodySlots, Vacations/Blocks/Exceptions).
//   3. Auto-Refresh alle 30 Sekunden (Polling für Stornos/Buchungen).

import { useState, useEffect, useRef, useCallback } from 'react';
import { toast } from 'sonner';
import {
  fetchCalendarSettingsApi,
  fetchTrainersApi,
  fetchServicesApi,
  fetchCustomersApi,
  fetchAnamneseStatusApi,
  fetchAppointmentsApi,
  fetchMindbodySlotsApi,
  fetchVacationsAndBlocksApi,
  fetchBirthdaysTodayApi,
  fetchPaymentColorsApi,
  fetchAvailableServicesApi,
  triggerMindbodySyncApi,
} from '@/services/appointmentsApi';
import { getDateRangeForView } from '@/components/appointments/calendarUtils';
import { isStudioOpenOnDate as utilIsStudioOpenOnDate } from '@/utils/calendarDateUtils';
import {
  DEFAULT_CALENDAR_START_HOUR,
  DEFAULT_CALENDAR_END_HOUR,
} from '@/components/appointments/calendarUtils';

export const useCalendarData = ({
  currentDate,
  view,
  showAllTrainers,
  /**
   * Optional Callback nach Appointments-Fetch. Wird mit dem gefundenen
   * Appointment aufgerufen, falls die Page via ?selected=<id> aufgerufen
   * wurde. Die Page übernimmt das Öffnen des Edit-Dialogs.
   */
  onAutoSelectAppointment,
}) => {
  // Calendar config
  const [calendarStartHour, setCalendarStartHour] = useState(
    DEFAULT_CALENDAR_START_HOUR
  );
  const [calendarEndHour, setCalendarEndHour] = useState(
    DEFAULT_CALENDAR_END_HOUR
  );
  const [studioOpeningHours, setStudioOpeningHours] = useState({});
  // Density-Aware Slot-Raster (07/2026) — vom Admin unter Settings konfigurierbar.
  const [densityConfig, setDensityConfig] = useState({
    enabled: true,
    threshold: 4,
    lowStep: 30,
    highStep: 15,
  });

  // Daten
  const [trainers, setTrainers] = useState([]);
  const [appointments, setAppointments] = useState([]);
  // Schalter "Abgesagte anzeigen" (Standard: aus → abgesagte Termine versteckt)
  const [showCancelled, setShowCancelled] = useState(false);
  const [mindbodySlots, setMindbodySlots] = useState([]);
  const [services, setServices] = useState([]);
  const [availableServices, setAvailableServices] = useState([]);
  // 07/2026 UX: Zusatzinfo warum Services gefiltert wurden — max_bookable_minutes
  // und Reason kommen aus dem `/available-services`-Endpoint und werden im
  // BookingDialog als deutscher Klartext gezeigt.
  const [availableServicesInfo, setAvailableServicesInfo] = useState({
    max_bookable_minutes: null,
    reason: '',
    filtered_out: [],
  });
  const [customers, setCustomers] = useState([]);
  const [anamneseStatus, setAnamneseStatus] = useState({});
  const [birthdaysToday, setBirthdaysToday] = useState([]);
  const [paymentColors, setPaymentColors] = useState({
    paid: '#10B981',
    pending: '#F59E0B',
    overdue: '#EF4444',
  });
  const [trainerVacations, setTrainerVacations] = useState([]);
  const [trainerBlocks, setTrainerBlocks] = useState([]);
  const [trainerExceptions, setTrainerExceptions] = useState([]);

  // UI-Status der Datenladung
  const [loading, setLoading] = useState(true);
  const [mbSyncing, setMbSyncing] = useState(false);
  // Iter 336: Live-Sync-Indikator — Zeitpunkt des letzten erfolgreichen
  // Appointments-Fetch (Polling/visibilitychange/manueller Reload).
  const [lastFetchedAt, setLastFetchedAt] = useState(null);

  // Refs
  const mbRefreshTimerRef = useRef(null);

  // ──────────────────────────────────────────────────────────────────
  //  Helpers
  // ──────────────────────────────────────────────────────────────────
  const isStudioOpenOnDate = useCallback(
    (date) => utilIsStudioOpenOnDate(date, studioOpeningHours),
    [studioOpeningHours]
  );

  // ──────────────────────────────────────────────────────────────────
  //  Fetchers
  // ──────────────────────────────────────────────────────────────────
  const fetchCalendarSettings = useCallback(async () => {
    const { calendar, openingHours } = await fetchCalendarSettingsApi();
    if (calendar) {
      if (calendar.calendar_start_hour !== undefined)
        setCalendarStartHour(calendar.calendar_start_hour);
      if (calendar.calendar_end_hour !== undefined)
        setCalendarEndHour(calendar.calendar_end_hour);
      // Density-Aware Config aus Backend übernehmen (mit Defaults)
      setDensityConfig({
        enabled: calendar.density_aware_enabled !== false,
        threshold: Number(calendar.density_threshold_appts) || 4,
        lowStep: Number(calendar.density_low_step) || 30,
        highStep: Number(calendar.density_high_step) || 15,
      });
    }
    setStudioOpeningHours(openingHours);
  }, []);

  const fetchTrainers = useCallback(async () => {
    try {
      const data = await fetchTrainersApi(currentDate, showAllTrainers);
      setTrainers(data);
      return data;
    } catch (error) {
      console.error('Error fetching trainers:', error);
      toast.error('Fehler beim Laden der Trainer');
      return [];
    }
  }, [currentDate, showAllTrainers]);

  const fetchAnamneseStatus = useCallback(async (customerIds) => {
    try {
      const data = await fetchAnamneseStatusApi(customerIds);
      setAnamneseStatus(data);
    } catch (error) {
      console.error('Error fetching anamnese status:', error);
    }
  }, []);

  const fetchCustomers = useCallback(async () => {
    try {
      const list = await fetchCustomersApi();
      setCustomers(list);
      if (list.length > 0) {
        fetchAnamneseStatus(list.map((c) => c.id));
      }
    } catch (error) {
      console.error('Error fetching customers:', error);
    }
  }, [fetchAnamneseStatus]);

  const fetchServices = useCallback(async () => {
    try {
      const servicesList = await fetchServicesApi();
      setServices(servicesList);
      setAvailableServices((curr) =>
        curr.length === 0 ? servicesList : curr
      );
    } catch (error) {
      console.error('Error fetching services:', error);
    }
  }, []);

  const fetchBirthdaysToday = useCallback(async () => {
    try {
      setBirthdaysToday(await fetchBirthdaysTodayApi());
    } catch (error) {
      console.error('Error fetching birthdays:', error);
    }
  }, []);

  const fetchPaymentColors = useCallback(async () => {
    const colors = await fetchPaymentColorsApi();
    if (colors) setPaymentColors(colors);
  }, []);

  const fetchVacationsAndBlocks = useCallback(
    async (trainersList) => {
      try {
        const list = trainersList || trainers;
        const { vacations, blocks, exceptions } =
          await fetchVacationsAndBlocksApi(list, currentDate);
        setTrainerVacations(vacations);
        setTrainerBlocks(blocks);
        setTrainerExceptions(exceptions);
      } catch (error) {
        console.error('Error fetching vacations/blocks/exceptions:', error);
      }
    },
    [trainers, currentDate]
  );

  const fetchAvailableServices = useCallback(
    async (trainerId, date, time) => {
      try {
        const data = await fetchAvailableServicesApi(trainerId, date, time);
        if (data.all_services_allowed) {
          setAvailableServices(services);
          setAvailableServicesInfo({
            max_bookable_minutes: null,
            reason: '',
            filtered_out: [],
          });
        } else {
          setAvailableServices(data.services);
          // Namen der rausgefilterten Services für den Tooltip berechnen
          const availableIds = new Set((data.services || []).map((s) => s.id));
          const filtered = (services || []).filter(
            (s) => !availableIds.has(s.id)
          );
          setAvailableServicesInfo({
            max_bookable_minutes: data.max_bookable_minutes ?? null,
            reason: data.reason || '',
            filtered_out: filtered.map((s) => ({
              id: s.id,
              name: s.name,
              duration_minutes: s.duration_minutes,
            })),
          });
          if (data.services.length === 0) {
            toast.warning(
              data.reason || 'Keine Services verfügbar zu dieser Zeit'
            );
          } else if (data.services.length < services.length) {
            toast.info(
              `${data.services.length} von ${services.length} Services verfügbar`
            );
          }
        }
        return data;
      } catch (error) {
        console.error('Error fetching available services:', error);
        setAvailableServices(services);
        setAvailableServicesInfo({
          max_bookable_minutes: null,
          reason: '',
          filtered_out: [],
        });
        return null;
      }
    },
    [services]
  );

  const fetchAppointments = useCallback(async () => {
    try {
      const { startDate, endDate } = getDateRangeForView(currentDate, view);
      const { appointments: appts, mbSlots } = await fetchAppointmentsApi(
        startDate,
        endDate,
        showCancelled
      );
      setAppointments(appts);
      setMindbodySlots(mbSlots);
      setLastFetchedAt(new Date());

      // Auto-Sync-Nachladung (06/2026): Der Slots-Abruf stösst im Backend einen
      // Hintergrund-Sync an (~10s Laufzeit). Einmalig nach 15s die MB-Slots
      // neu laden, damit frische Mindbody-Änderungen (Buchung/Löschung) OHNE
      // manuellen Reload im Kalender erscheinen.
      if (mbRefreshTimerRef.current) clearTimeout(mbRefreshTimerRef.current);
      mbRefreshTimerRef.current = setTimeout(async () => {
        try {
          const slots = await fetchMindbodySlotsApi(startDate, endDate);
          setMindbodySlots(slots);
        } catch {
          // still failing silently — nächster regulärer Fetch korrigiert
        }
      }, 15000);

      // Auto-Select (UI-Side-Effect) der Page überlassen
      if (onAutoSelectAppointment) {
        onAutoSelectAppointment(appts);
      }
    } catch (error) {
      console.error('Error fetching appointments:', error);
    }
  }, [currentDate, view, showCancelled, onAutoSelectAppointment]);

  const handleMindbodySync = useCallback(async () => {
    setMbSyncing(true);
    try {
      const stats = await triggerMindbodySyncApi();
      if (stats.success) {
        toast.success(
          `Mindbody synchronisiert: ${stats.appointments_fetched ?? 0} Termine, ${
            stats.unavailabilities_fetched ?? 0
          } Pausen geladen`
        );
      } else {
        toast.error(
          `Mindbody-Sync fehlgeschlagen: ${stats.error || 'Unbekannter Fehler'}`
        );
      }
      await fetchAppointments();
    } catch (error) {
      console.error('Mindbody sync error:', error);
      toast.error('Mindbody-Sync fehlgeschlagen');
    } finally {
      setMbSyncing(false);
    }
  }, [fetchAppointments]);

  // ──────────────────────────────────────────────────────────────────
  //  Auto-Load: Statische Daten beim Mount
  // ──────────────────────────────────────────────────────────────────
  useEffect(() => {
    (async () => {
      try {
        await Promise.all([
          fetchCalendarSettings(),
          fetchServices(),
          fetchCustomers(),
          fetchBirthdaysToday(),
          fetchPaymentColors(),
        ]);
      } catch (error) {
        console.error('Error fetching static data:', error);
      }
    })();
  }, []);

  // ──────────────────────────────────────────────────────────────────
  //  Auto-Load: Dynamische Daten + 30s-Polling
  // ──────────────────────────────────────────────────────────────────
  useEffect(() => {
    const load = async () => {
      setLoading(true);
      try {
        const trainersData = await fetchTrainers();
        await Promise.all([
          fetchAppointments(),
          fetchVacationsAndBlocks(trainersData),
        ]);
      } catch (error) {
        console.error('Error fetching schedule data:', error);
      } finally {
        setLoading(false);
      }
    };
    load();

    // Poll für customer cancellations
    const pollInterval = setInterval(() => {
      fetchAppointments();
    }, 30000);

    // Iter 336: Sofortiger Refresh beim Tab-/Window-Wechsel zurück. Wenn ein
    // anderer Admin/Trainer parallel verschiebt, sieht der zurückkehrende User
    // den neuesten Stand ohne F5. Reagiert auf visibilitychange (Tab-Switch)
    // und focus (Window-Aktivierung, Push-Click).
    const onVisible = () => {
      if (document.visibilityState === 'visible') {
        fetchAppointments();
      }
    };
    document.addEventListener('visibilitychange', onVisible);
    window.addEventListener('focus', onVisible);

    return () => {
      clearInterval(pollInterval);
      document.removeEventListener('visibilitychange', onVisible);
      window.removeEventListener('focus', onVisible);
    };
  }, [currentDate, view, showAllTrainers, showCancelled]);

  return {
    // Calendar config
    calendarStartHour,
    calendarEndHour,
    studioOpeningHours,
    densityConfig,
    isStudioOpenOnDate,
    // Data
    trainers,
    setTrainers,
    appointments,
    setAppointments,
    showCancelled,
    setShowCancelled,
    mindbodySlots,
    setMindbodySlots,
    services,
    availableServices,
    setAvailableServices,
    customers,
    anamneseStatus,
    setAnamneseStatus,
    birthdaysToday,
    paymentColors,
    trainerVacations,
    trainerBlocks,
    trainerExceptions,
    // UI status
    loading,
    mbSyncing,
    lastFetchedAt,
    // Fetchers (imperativ)
    fetchCalendarSettings,
    fetchTrainers,
    fetchAppointments,
    fetchServices,
    fetchCustomers,
    fetchAnamneseStatus,
    fetchVacationsAndBlocks,
    fetchAvailableServices,
    fetchBirthdaysToday,
    fetchPaymentColors,
    handleMindbodySync,
  };
};

export default useCalendarData;
